import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import { selectTicketIsFetched } from 'flow/selectors/TicketsSelectors';
import { fetchTicket } from 'flow/actions/tickets-action';
import { useModal } from 'flow/contexts/ModalProvider';
import { CallRecordKey, QueueKey } from 'flow/types/EntityKeys';
import { getContactFromCallRecord } from 'flow/utils/call-record-utils';
import { makeSelectCallRecordDisplayName } from 'flow/selectors/call-records-selectors';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import CallHistoryContextMenu from 'flow/components/context-menus/CallHistoryContextMenu';
import ContextMenu from 'flow/components/ContextMenu';
import useAppContext from 'flow/hooks/useAppContext';
import { getMoreOptionsLabel } from 'flow/utils/a11y-utils';
import Divider from 'flow/components/ui-components/Divider';
import ButtonCreateShowTicket from 'flow/components/pbx/v2/misc/ButtonCreateShowTicket/ButtonCreateShowTicket';
import { SegmentContextType } from 'flow/components/call-history-v2/types';
import MenuItemAssignTo from './MenuItemAssignTo';

interface Props {
    callRecordKey: CallRecordKey;
    queueKey?: QueueKey;
    segmentContext: SegmentContextType;
    setActive: (active: boolean) => void;
}

const CallRecordContextMenu = ({
    callRecordKey,
    queueKey,
    segmentContext,
    setActive,
}: Props) => {
    const { i18next } = useAppContext();
    const selectDisplayName = useMemo(makeSelectCallRecordDisplayName, []);
    const displayName = useAppSelector((state) =>
        selectDisplayName(state, callRecordKey),
    );

    return (
        <ContextMenu
            setActive={setActive}
            triggerLabel={getMoreOptionsLabel(i18next, displayName)}
        >
            <CallRecordContextMenuContent
                callRecordKey={callRecordKey}
                queueKey={queueKey}
                segmentContext={segmentContext}
            />
        </ContextMenu>
    );
};

export default CallRecordContextMenu;

const CallRecordContextMenuContent = ({
    callRecordKey,
    queueKey,
    segmentContext,
}: {
    callRecordKey: CallRecordKey;
    queueKey?: QueueKey;
    segmentContext: SegmentContextType;
}) => {
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const ownCountryCode = useAppSelector(selectUserCountryCode);
    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );
    const ticketKey = callRecord?.get('omniTicketEntityKey');

    const ticketIsFetched = useAppSelector((state) =>
        ticketKey ? selectTicketIsFetched(state, ticketKey) : false,
    );

    useEffect(() => {
        if (ticketKey && !ticketIsFetched) {
            dispatch(fetchTicket(ticketKey));
        }
    }, [dispatch, ticketKey, ticketIsFetched]);

    if (!callRecord) return null;

    return (
        <>
            <CallHistoryContextMenu
                callRecordKey={callRecordKey}
                onClickCreateContact={() =>
                    openModal('createContact', {
                        contact: getContactFromCallRecord(callRecord),
                        countryCode: ownCountryCode?.toUpperCase(),
                    })
                }
            />
            {queueKey && (
                <>
                    <Divider className="my-2" />
                    <ButtonCreateShowTicket
                        queueKey={queueKey}
                        callRecordKey={callRecordKey}
                        ticketKey={ticketKey}
                        callerNumber={callRecord.getIn(['number', 'number'])}
                        callTimestamp={callRecord.get('connected')}
                        buttonType="menuItem"
                        segmentContext={segmentContext}
                    />
                    <MenuItemAssignTo
                        callRecordKey={callRecordKey}
                        queueKey={queueKey}
                    />
                </>
            )}
        </>
    );
};
